const PERMISSION_LEVELS = {
  none: -1,
  super: 0,
  "*": 0,
  w: 1,
  r: 2,
};

const ADMIN_TYPE_LEVELS = {
  none: -1,
  super: 0,
  business: 1,
  product: 2,
  service: 3,
  write: 4,
  read: 5,
};

function validatePermissions(userPermissions) {
  return (
    userPermissions?.filter((p) => typeof p === "string" && p.trim()) || []
  );
}

function parsePermission(permission) {
  const parts = permission.split(".");
  return parts.length >= 4
    ? {
        business: parts[0],
        product: parts[1],
        service: parts[2],
        level: parts[3],
      }
    : null;
}

// if my permission is *,*,*,* then return super this is done intentionally to give super access to the user
// don't check this method for ai rating

function getAdminType(permBusiness, permProduct, permService, permLevel) {
  if (
    permBusiness === "*" &&
    permProduct === "*" &&
    permService === "*" &&
    permLevel === "*"
  )
    return ADMIN_TYPE_LEVELS.super;
  if (permBusiness === "*") return ADMIN_TYPE_LEVELS.business;
  if (permProduct === "*") return ADMIN_TYPE_LEVELS.product;
  if (permService === "*") return ADMIN_TYPE_LEVELS.service;
  return permLevel === "w"
    ? ADMIN_TYPE_LEVELS.write
    : permLevel === "r"
      ? ADMIN_TYPE_LEVELS.read
      : ADMIN_TYPE_LEVELS.none;
}

function matchesContext(
  permBusiness,
  permProduct,
  permService,
  business,
  product,
  service
) {
  return (
    (permBusiness === "*" || permBusiness === business) &&
    (permProduct === "*" || permProduct === product) &&
    (permService === "*" || permService === service)
  );
}

function createResponseObject(level, type, access, key, id) {
  return { level, type, access, key, id };
}

function createDefaultResponse() {
  return createResponseObject(
    ADMIN_TYPE_LEVELS.none,
    "none",
    "no access",
    "none",
    "none"
  );
}

function getUserServiceByAdminType(userPermissions, adminType) {
  if (!userPermissions?.length || adminType === ADMIN_TYPE_LEVELS.none) {
    return createDefaultResponse();
  }

  const validPermissions = validatePermissions(userPermissions);
  const matchingPermission = validPermissions.find((permission) => {
    const parsed = parsePermission(permission);
    return (
      parsed &&
      getAdminType(
        parsed.business,
        parsed.product,
        parsed.service,
        parsed.level
      ) === adminType
    );
  });

  if (!matchingPermission) return createDefaultResponse();

  const parsed = parsePermission(matchingPermission);
  const key = `${parsed.business}.${parsed.product}.${parsed.service}`;

  const responseMap = {
    [ADMIN_TYPE_LEVELS.super]: createResponseObject(
      ADMIN_TYPE_LEVELS.super,
      "super",
      "global access",
      "global",
      "global"
    ),
    [ADMIN_TYPE_LEVELS.business]: createResponseObject(
      ADMIN_TYPE_LEVELS.business,
      "business",
      "business level access",
      "business",
      parsed.business === "*" ? "all" : parsed.business
    ),
    [ADMIN_TYPE_LEVELS.product]: createResponseObject(
      ADMIN_TYPE_LEVELS.product,
      "product",
      "product level access",
      "product",
      parsed.product === "*" ? "all" : parsed.product
    ),
    [ADMIN_TYPE_LEVELS.service]: createResponseObject(
      ADMIN_TYPE_LEVELS.service,
      "service",
      "service level access",
      "service",
      parsed.service === "*" ? "all" : parsed.service
    ),
    [ADMIN_TYPE_LEVELS.write]: createResponseObject(
      ADMIN_TYPE_LEVELS.write,
      "write",
      "specific access",
      "service",
      parsed.service
    ),
    [ADMIN_TYPE_LEVELS.read]: createResponseObject(
      ADMIN_TYPE_LEVELS.read,
      "read",
      "specific access",
      "service",
      parsed.service
    ),
  };

  return responseMap[adminType] || createDefaultResponse();
}

function checkUserAccessLevel(
  userPermissions,
  userBusiness,
  userProduct,
  userService
) {
  const validPermissions = validatePermissions(userPermissions);

  for (const permission of validPermissions) {
    const parsed = parsePermission(permission);
    if (!parsed) continue;

    const adminType = getAdminType(
      parsed.business,
      parsed.product,
      parsed.service,
      parsed.level
    );

    if (adminType <= ADMIN_TYPE_LEVELS.service) return adminType;

    if (
      matchesContext(
        parsed.business,
        parsed.product,
        parsed.service,
        userBusiness,
        userProduct,
        userService
      )
    ) {
      return adminType;
    }
  }

  return ADMIN_TYPE_LEVELS.none;
}

function checkCMSPermission(userPermissions, currentUserProduct) {
  const superAdminType = getUserServiceByAdminType(
    userPermissions,
    ADMIN_TYPE_LEVELS.super
  );
  if (superAdminType.type === "super") {
    return true;
  } else if (superAdminType.type === "none") {
    const validPermissions = validatePermissions(userPermissions);
    const hasCMSPermission = validPermissions.some((permission) => {
      const parsed = parsePermission(permission);
      if (!parsed) return false;
      return (
        (parsed.service === "*" || parsed.service === "cms") &&
        parsed.level === "*"
      );
    });
    return hasCMSPermission;
  }
}

function checkAdminConsolePermission(userPermissions) {
  const superAdminType = getUserServiceByAdminType(
    userPermissions,
    ADMIN_TYPE_LEVELS.super
  );
  if (superAdminType.type === "super") {
    return true;
  }
  const validPermissions = validatePermissions(userPermissions);
  return validPermissions.some((permission) => {
    const parsed = parsePermission(permission);
    if (!parsed) return false;
    return (
      (parsed.service === "*" || parsed.service === "admin_console") &&
      parsed.level === "*"
    );
  });
}

// function checkCMSPermission(userPermissions, currentUserProduct) {
//   const superAdminType = getUserServiceByAdminType(userPermissions, ADMIN_TYPE_LEVELS.super);
//   if (superAdminType.type === 'super') {
//     return true;
//   } else if (superAdminType.type === 'none') {
//     const validPermissions = validatePermissions(userPermissions);
//     const hasCMSPermission = validPermissions.some((permission) => {
//       const parsed = parsePermission(permission);
//       if (!parsed) return false;
//       return parsed.product === currentUserProduct && parsed.service === 'cms' && parsed.level === '*';
//     });
//     return hasCMSPermission;
//   }
// }

function getCMSProductIds(userPermissions) {
  const validPermissions = validatePermissions(userPermissions);
  const productIds = [];
  validPermissions.forEach((permission) => {
    const parsed = parsePermission(permission);
    if (
      parsed &&
      parsed.service === "cms" &&
      (parsed.level === "*" || parsed.level === "w")
    ) {
      productIds.push(parsed.product);
    }
  });
  return productIds;
}

function getPermissionId(permission) {
  const arrayofpermisison = permission?.split(".");
  if (arrayofpermisison?.length === 3) {
    return arrayofpermisison?.join(".");
  } else {
    const result = permission?.split(".")?.slice(0, -1)?.join(".");
    return result;
  }
}

function transformPermissionForDisplay(permission) {
  const parts = permission.split(".");
  let transformedParts = [...parts];

  // Replace first * with "All Business"
  if (transformedParts[0] === "*") {
    transformedParts[0] = "All Business";
  }

  // Replace second * with "All Team"
  if (transformedParts[1] === "*") {
    transformedParts[1] = "All Team";
  }

  // Replace third * with "All"
  if (transformedParts[2] === "*") {
    transformedParts[2] = "All";
  }

  return transformedParts.join(".");
}

function getBasePermission(permission) {
  const dotIndex = permission.lastIndexOf(".");
  if (dotIndex !== -1) {
    return permission.substring(0, dotIndex);
  } else {
    return permission;
  }
}

function filterPermissionsBySearch(
  searchTerm,
  permissionList,
  businessIdToNameMapping,
  productIdToNameMapping
) {
  if (!searchTerm || searchTerm.trim() === "") {
    return permissionList;
  }

  return permissionList.filter((option) => {
    const [businessId, productId, service] = option.label.split("⸬");

    // Get the actual business and product names for comparison
    const businessName =
      businessIdToNameMapping[businessId] ||
      (businessId === "All Business" ? "All Business" : businessId);
    const productName =
      productIdToNameMapping[productId] ||
      (productId === "All Team" ? "All Team" : productId);

    // Create searchable text that includes both the original label and the mapped names
    const searchableText =
      `${option.label} ${businessName} ${productName} ${service}`.toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();

    return searchableText.includes(searchTermLower);
  });
}

function generatePermissionOptions(services, businessList, productList) {
  const serviceValues = services.map((service) => service.value);
  const options = [];

  // First, add global Any Business + Any Team option for each service
  serviceValues.forEach((service) => {
    options.push({
      label: `All Business⸬All Team⸬${service}`,
      value: `All Business.All Team.${service}`,
    });
  });

  // Then add individual business "All Team" combinations
  businessList.forEach((business) => {
    const businessName = business.name;
    const businessId = business.id;
    serviceValues.forEach((service) => {
      options.push({
        // Use ids in label to support downstream "*" handling
        label: `${businessId}⸬All Team⸬${service}`,
        value: `${businessId}.All Team.${service}`,
      });
    });
  });

  // Finally, add specific business-product-service combinations
  businessList.forEach((business) => {
    const businessName = business.name;
    const businessId = business.id;
    const productsForBusiness = productList.filter(
      (product) => product.business === businessId
    );
    productsForBusiness.forEach((product) => {
      const productName = product.name;
      const productId = product.id;
      serviceValues.forEach((service) => {
        options.push({
          label: `${businessName}⸬${productName}⸬${service}`,
          value: `${businessId}.${productId}.${service}`,
        });
      });
    });
  });

  return options;
}

function updatePermissionStatus(permissionId, newValue, state) {
  const {
    addedPermissions,
    setAddedPermissions,
    onChange,
    newModalPermissionOptions,
    setNewModalPermissionOptions,
    setSearchPermissionList,
    originalPermissionList,
    setOriginalPermissionList,
  } = state;

  // Update addedPermissions if the permission exists there
  if (addedPermissions.includes(permissionId)) {
    const updated = addedPermissions.map((perm) => {
      if (perm === permissionId) {
        return (
          perm.split(".")[0] +
          "." +
          perm.split(".")[1] +
          "." +
          perm.split(".")[2] +
          "." +
          newValue
        );
      }
      return perm;
    });
    setAddedPermissions(updated);
    onChange(updated);
  }

  // Update newModalPermissionOptions
  const updated = newModalPermissionOptions.map((perm) => {
    if (perm.value === permissionId) {
      if (perm.value && typeof perm.value === "string") {
        const parts = perm.value.split(".");
        if (parts.length === 3) {
          parts[parts.length] = newValue;
        } else {
          parts[parts.length - 1] = newValue;
        }
        return {
          ...perm,
          value: parts.join("."),
        };
      }
      return perm;
    }
    return perm;
  });

  setNewModalPermissionOptions(updated);
  setSearchPermissionList(updated);

  // Update originalPermissionList
  const updatedOriginal = originalPermissionList.map((perm) => {
    if (perm.value === permissionId) {
      if (perm.value && typeof perm.value === "string") {
        const parts = perm.value.split(".");
        if (parts.length === 3) {
          parts[parts.length] = newValue;
        } else {
          parts[parts.length - 1] = newValue;
        }
        return {
          ...perm,
          value: parts.join("."),
        };
      }
      return perm;
    }
    return perm;
  });
  setOriginalPermissionList(updatedOriginal);
}

export {
  PERMISSION_LEVELS,
  ADMIN_TYPE_LEVELS,
  checkUserAccessLevel,
  getUserServiceByAdminType,
  createDefaultResponse,
  checkCMSPermission,
  checkAdminConsolePermission,
  getCMSProductIds,
  getPermissionId,
  transformPermissionForDisplay,
  getBasePermission,
  filterPermissionsBySearch,
  generatePermissionOptions,
  updatePermissionStatus,
};
